import { ButtonNavBar, IButtonNavBarItem } from '@app/ButtonNavBar';
import { PageLayout } from '@app/PageLayout';
import {
  Breadcrumb,
  Button,
  ComponentType,
  Drawer,
  Grid,
  Heading,
  HeadingType,
  IBreadcrumbItem,
  Icon,
  Loader,
  Scrollbar,
  useArrow,
} from '@gcl/react-core';
import * as React from 'react';
import { EUserProfilePreferenceAttributeName } from '@userProfile/userProfilePreferences.constants';
import { useBreakPoints } from './breakpoints.hooks';
import { useDrawerOpenState } from './pageLayout.hooks';
import { IFilter } from './searchFilter/searchFilter.models';
import './SearchPageBase.scss';
import { TableContentLoader } from './TableContentLoader';

const drawerWidthSmall = '90%';
const drawerWidthMiddle = '300px';
const drawerWidthLarge = '380px';
export interface ISearchPageBaseProps {
  breadcrumbItems?: IBreadcrumbItem[];
  title: string;
  isTableRefetching: boolean;
  isTableLoading: boolean;
  buttonNavBarItem?: IButtonNavBarItem[];
  buttonNavBarItemRight?: IButtonNavBarItem[];
  searchFilter?: React.ReactElement;
  appliedSearchFilter?: IFilter;
  noContentLabel?: string;
  onRenderTableHeaderComponent?: () => React.ReactElement;
  onRenderTable: (isMenuHidden: boolean) => React.ReactElement;
  legend?: React.ReactElement;
  data: any[] | undefined;
  shouldUpdateArrow?: boolean;
  headerElement?: React.ReactElement;
  userPreferencePageSizeKey?: EUserProfilePreferenceAttributeName;
  isGlobalPage?: boolean;
}

export const SearchPageBase: React.FunctionComponent<ISearchPageBaseProps> = ({
  breadcrumbItems,
  title,
  isTableRefetching,
  isTableLoading,
  buttonNavBarItem,
  buttonNavBarItemRight = [],
  searchFilter,
  onRenderTableHeaderComponent,
  onRenderTable,
  legend,
  data,
  shouldUpdateArrow = false,
  appliedSearchFilter = {},
  noContentLabel,
  headerElement,
  userPreferencePageSizeKey,
  isGlobalPage = false,
}) => {
  const { isXs, isSm, isMd, isLg, isXl } = useBreakPoints();

  const isMobileSize = isSm || isXs || isMd;
  const desktopSize = isLg || isXl ? drawerWidthMiddle : drawerWidthLarge;
  const [isDrawerOpen, setIsDrawerOpen] = useDrawerOpenState(undefined);
  const updateArrow = useArrow();

  const updateArrowWithDelay = async () => {
    const intervalId = setInterval(() => {
      updateArrow();
    }, 150);

    setTimeout(() => {
      clearInterval(intervalId);
    }, 400);
  };

  React.useEffect(() => {
    if (!shouldUpdateArrow) return;
    updateArrowWithDelay();
  }, [shouldUpdateArrow, isDrawerOpen]);

  const localIsDrawerOpen = isDrawerOpen && !!searchFilter;

  const buttonNavBarItemRightLocal = React.useMemo(() => {
    const items = [...(buttonNavBarItemRight ?? [])];

    if (searchFilter)
      items.push({
        text: 'Filters',
        onClick: () => {
          setIsDrawerOpen(!isDrawerOpen);
        },
        iconName: 'Funnel',
        componentType: ComponentType.Secondary,
      });

    return items;
  }, [buttonNavBarItemRight, setIsDrawerOpen, isDrawerOpen]);
  return (
    <PageLayout
      isGlobalPage={isGlobalPage}
      renderMainContent={({ isMenuHidden }) => (
        <>
          <Drawer
            PaperProps={{
              style: {
                width: isMobileSize ? drawerWidthSmall : desktopSize,
              },
            }}
            variant={isMobileSize ? 'temporary' : 'persistent'}
            setOpen={setIsDrawerOpen}
            open={localIsDrawerOpen}
          >
            <Grid container flexWrap="nowrap" alignItems="center">
              <Button
                type={ComponentType.Secondary}
                className="gcl-ml-md gcl-my-xs"
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
              >
                <Icon iconName="ChevronRight" />
              </Button>
              <Heading as={HeadingType.h6} className="gcl-ml-sm" style={{ marginBottom: 0 }}>
                Filters
              </Heading>
            </Grid>
            <Scrollbar>
              <div className="gcl-gems-search-page-base-filter-container">{searchFilter}</div>
            </Scrollbar>
          </Drawer>
          <Grid
            sx={[
              {
                transition: 'margin-right 0.5s ease-in-out',
              },
              localIsDrawerOpen && { marginRight: `calc(${desktopSize} + 35px)` },
            ]}
            className={`${isMobileSize ? 'gcl-m-md gcl-pb-xl' : 'gcl-px-md gcl-mx-xl gcl-my-md'}`}
          >
            <>
              <div className="gcl-gems-search-page-base-breadcrumb-container">
                {breadcrumbItems && breadcrumbItems.length > 0 ? (
                  <div className="gcl-pb-m gcl-mr-md">
                    <Breadcrumb items={breadcrumbItems} />
                  </div>
                ) : undefined}
                {data && isTableRefetching ? (
                  <>
                    <Loader className="gcl-pb-m" small text="Fetching latest data from server" />
                  </>
                ) : null}
              </div>
              <Heading as={HeadingType.h1} styledAs={HeadingType.h3} className="gcl-pb-lg">
                {title}
              </Heading>
              {data && (
                <>
                  {headerElement}
                  {(buttonNavBarItem && buttonNavBarItem.length > 0) || !!searchFilter ? (
                    <ButtonNavBar
                      items={buttonNavBarItem || []}
                      className="gcl-gems-search-page-base-button-nav-bar"
                      rightItems={buttonNavBarItemRightLocal}
                    />
                  ) : undefined}
                  {onRenderTableHeaderComponent ? onRenderTableHeaderComponent() : undefined}

                  {data.length > 0 && (
                    <div className="gcl-gems-search-page-base-table-container">{onRenderTable(isMenuHidden)}</div>
                  )}
                  {data.length <= 0 && Object.keys(appliedSearchFilter).length <= 0 && (
                    <div className="gcl-gems-search-page-base-no-content">
                      {noContentLabel ?? 'No item created yet'}
                    </div>
                  )}
                  {data.length <= 0 && Object.keys(appliedSearchFilter).length > 0 && (
                    <>
                      <div className="gcl-gems-search-page-base-table-container">{onRenderTable(isMenuHidden)}</div>
                      <div className="gcl-gems-search-page-base-no-content">
                        No records found for the search criteria entered, please refine your search.
                      </div>
                    </>
                  )}
                </>
              )}
              {!data && isTableLoading && <TableContentLoader />}
              {legend && (
                <div className="gcl-mt-md">
                  <div>Legend:</div>
                  {legend}
                </div>
              )}
            </>
          </Grid>
        </>
      )}
    />
  );
};
